/**
 * PWA Notifications & Badge Manager
 * Handles:
 * 1. App badge count (new responses since last visit)
 * 2. FCM push notification registration
 * 3. Browser notification permission
 */

class PWANotificationManager {
  constructor() {
    this.fcmToken = null;
    this.messaging = null;
    this.STORAGE_KEY = 'quikrsvp-last-seen-responses';
    this.FCM_TOKEN_KEY = 'quikrsvp-fcm-token';
    this.VAPID_KEY = 'BGNzG7m13WeALshm8L9RzpvSpiEVdj7qSnhdDIfw6R6iO-J6lMoc5h7DDz--1hi39YmZ5AbPPJcOJlSEw7NAqeE';
  }

  /**
   * Initialize on dashboard load - check for new responses and update badge
   */
  async init() {
    try {
      // Badge counts and the opt-in banner belong to the dashboard. The script
      // is also loaded on My Projects purely so the notifications modal can
      // call enablePush()/disablePush(), and booting the badge there would add
      // a Firestore read per page load for nothing.
      if (!window.location.pathname.includes('dashboard')) return;
      // The demo dashboard blocks every Firestore write by design (dashboard.html's
      // write blocker rejects with "Failed to save to database"), so a visitor
      // who had allowed notifications elsewhere got their token refresh
      // rejected here and reported as a push fault (2026-10-05). Push means
      // nothing on a sample event anyway.
      if (this.isDemo()) return;

      // Wait for auth
      const user = await this.waitForAuth();
      if (!user) return;

      // On very first run for this user, seed lastSeen to current total so
      // existing historical responses aren't all shown as "new".
      await this.ensureBadgeState(user.uid);

      // Update badge based on response counts
      await this.updateBadge(user.uid);

      // Mark responses as seen only after the dashboard has actually been
      // visible for a moment — prevents the badge from clearing when the
      // user just clicks the app and immediately switches away.
      this.scheduleMarkAsSeen(user.uid);

      // Set up FCM if VAPID key is configured and firebase.messaging is available
      if (this.VAPID_KEY && typeof firebase !== 'undefined' && firebase.messaging) {
        await this.initFCM(user);
      }
    } catch (err) {
      console.warn('PWA notifications init error:', err);
    }
  }

  /**
   * Mark responses as seen once the user has had the dashboard visible for
   * a grace period. Resets and re-arms if the tab becomes hidden.
   */
  scheduleMarkAsSeen(uid) {
    const GRACE_MS = 5000;
    let timer = null;

    const armTimer = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        if (document.visibilityState === 'visible') {
          this.markResponsesSeen(uid);
        }
      }, GRACE_MS);
    };
    const cancelTimer = () => {
      if (timer) { clearTimeout(timer); timer = null; }
    };

    if (document.visibilityState === 'visible') armTimer();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') armTimer();
      else cancelTimer();
    });
  }

  /**
   * First-run setup: if no badge state exists for this user yet, seed it from
   * current total (or migrate from legacy localStorage key). This avoids
   * nuking a brand-new install with a huge "all historical responses" badge.
   *
   * Also seeds totalResponseCount on the badge state doc so subsequent reads
   * (badge update, server push payload) don't have to scan rsvp_forms. Once
   * seeded, server-side bumpUserBadgeCount keeps it current.
   */
  async ensureBadgeState(uid) {
    const db = window.firebaseDb || window.db;
    if (!db) return;
    try {
      const doc = await db.collection('user_badge_state').doc(uid).get();
      const data = doc.exists ? doc.data() : null;
      const hasTotal = data && typeof data.totalResponseCount === 'number';
      if (data && hasTotal) return;

      const legacyLastSeen = parseInt(localStorage.getItem(this.STORAGE_KEY) || '0', 10);
      const currentTotal = await this.getTotalResponseCount(uid);
      const initialLastSeen = data && typeof data.lastSeenCount === 'number'
        ? data.lastSeenCount
        : (legacyLastSeen > 0 ? legacyLastSeen : currentTotal);

      const seed = { totalResponseCount: currentTotal };
      if (!data) {
        seed.lastSeenCount = initialLastSeen;
        seed.createdAt = firebase.firestore.FieldValue.serverTimestamp();
      } else if (!hasTotal && data.lastSeenCount === undefined) {
        seed.lastSeenCount = initialLastSeen;
      }
      await db.collection('user_badge_state').doc(uid).set(seed, { merge: true });
    } catch (e) {
      // permission-denied is expected for users whose Firestore auth context
      // hasn't propagated yet — fallbacks already handle this path. Surface
      // other errors so real bugs aren't masked.
      if (e?.code !== 'permission-denied') {
        console.warn('ensureBadgeState failed:', e);
      }
    }
  }

  /**
   * Read the badge state doc — single doc read containing both lastSeenCount
   * and the denormalized totalResponseCount. Replaces the per-call
   * O(forms-per-user) rsvp_forms scan that caused the late-April 2026 read
   * spike. ensureBadgeState() seeds totalResponseCount on first run.
   */
  async getBadgeState(uid) {
    const db = window.firebaseDb || window.db;
    if (!db) {
      return {
        lastSeenCount: parseInt(localStorage.getItem(this.STORAGE_KEY) || '0', 10),
        totalResponseCount: null
      };
    }
    try {
      const doc = await db.collection('user_badge_state').doc(uid).get();
      if (doc.exists) {
        const data = doc.data();
        return {
          lastSeenCount: data.lastSeenCount || 0,
          totalResponseCount: typeof data.totalResponseCount === 'number'
            ? data.totalResponseCount
            : null
        };
      }
    } catch (e) {
      if (e?.code !== 'permission-denied') {
        console.warn('getBadgeState failed, using localStorage fallback:', e);
      }
    }
    return {
      lastSeenCount: parseInt(localStorage.getItem(this.STORAGE_KEY) || '0', 10),
      totalResponseCount: null
    };
  }

  /**
   * Wait for Firebase Auth to be ready
   */
  waitForAuth() {
    return new Promise((resolve) => {
      const auth = window.firebaseAuth;
      if (!auth) return resolve(null);

      if (auth.currentUser) return resolve(auth.currentUser);

      const unsubscribe = auth.onAuthStateChanged((user) => {
        unsubscribe();
        resolve(user);
      });

      // Timeout after 5s
      setTimeout(() => resolve(null), 5000);
    });
  }

  /**
   * Slow-path total: scans every form the user owns. Only called on first run
   * (ensureBadgeState seed) — after that, totalResponseCount on the badge
   * state doc is kept current by server-side bumpUserBadgeCount on every
   * response write, and the regular code path uses getBadgeState() instead.
   */
  async getTotalResponseCount(uid) {
    const db = window.firebaseDb || window.db;
    if (!db) return 0;

    // firestore-audit-allow: seed-only fallback. Runs once per user post-deploy
    // until ensureBadgeState writes totalResponseCount to user_badge_state;
    // backfill-user-badge-counts.js seeds existing users in bulk.
    const snapshot = await db.collection('rsvp_forms')
      .where('owner', '==', uid)
      .get();

    let total = 0;
    snapshot.forEach(doc => {
      total += doc.data().responseCount || 0;
    });

    return total;
  }

  /**
   * Update the PWA app badge with new response count
   */
  async updateBadge(uid) {
    const state = await this.getBadgeState(uid);
    const currentTotal = state.totalResponseCount !== null
      ? state.totalResponseCount
      : await this.getTotalResponseCount(uid);
    const newCount = Math.max(0, currentTotal - state.lastSeenCount);

    if ('setAppBadge' in navigator) {
      if (newCount > 0) {
        navigator.setAppBadge(newCount).catch(() => {});
        console.log(`🔴 Badge set: ${newCount} new responses`);
      } else {
        navigator.clearAppBadge().catch(() => {});
      }
    }

    return newCount;
  }

  /**
   * Mark all current responses as "seen" - call when user views their dashboard.
   * Writes to Firestore (shared source of truth with server) and mirrors to
   * localStorage as a synchronous fallback.
   */
  async markResponsesSeen(uid) {
    const state = await this.getBadgeState(uid);
    const currentTotal = state.totalResponseCount !== null
      ? state.totalResponseCount
      : await this.getTotalResponseCount(uid);
    const db = window.firebaseDb || window.db;
    if (db) {
      try {
        await db.collection('user_badge_state').doc(uid).set({
          lastSeenCount: currentTotal,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        }, { merge: true });
      } catch (e) {
        if (e?.code !== 'permission-denied') {
          console.warn('markResponsesSeen Firestore write failed:', e);
        }
      }
    }
    localStorage.setItem(this.STORAGE_KEY, currentTotal.toString());

    if ('clearAppBadge' in navigator) {
      navigator.clearAppBadge().catch(() => {});
    }
  }

  /**
   * Initialize Firebase Cloud Messaging for push notifications
   */
  async initFCM(user) {
    try {
      // Check if firebase.messaging is available
      if (typeof firebase === 'undefined' || !firebase.messaging) {
        console.log('FCM: firebase.messaging not loaded');
        return;
      }

      this.messaging = firebase.messaging();

      // Check if we already have a token
      const existingToken = localStorage.getItem(this.FCM_TOKEN_KEY);

      // Request notification permission
      const permission = Notification.permission;
      if (permission === 'denied') {
        console.log('FCM: Notifications blocked by user');
        return;
      }

      if (permission === 'default') {
        // If running as installed PWA, request permission directly (user already chose to install)
        if (window.matchMedia('(display-mode: standalone)').matches) {
          const result = await Notification.requestPermission();
          if (result === 'granted') {
            await this.getAndSaveToken(user);
          }
          return;
        }
        // Otherwise show a subtle opt-in prompt
        console.log('FCM: Permission not yet granted. Will ask when user opts in.');
        this.showNotificationOptIn();
        return;
      }

      // Permission is 'granted' - get/refresh token
      await this.getAndSaveToken(user);

      // Handle foreground messages
      this.messaging.onMessage((payload) => {
        console.log('FCM foreground message:', payload);
        // A support-chat reply on an open page: the chat widget already shows
        // it (and badges its launcher), so no RSVP toast and no badge update.
        if (payload && payload.data && payload.data.type === 'support_reply') return;
        this.showInAppNotification(payload);
        // Update badge
        this.updateBadge(user.uid);
      });

    } catch (err) {
      console.warn('FCM init error:', err);
    }
  }

  /**
   * Get FCM token and save to Firestore
   */
  /**
   * The service worker FCM will subscribe through, guaranteed ACTIVE.
   *
   * pushManager.subscribe() throws "Subscription failed - no active Service
   * Worker" against a registration that is still installing, and letting the
   * SDK auto-register at click time hits exactly that: it registers and
   * subscribes in the same breath, with nothing in between for the worker to
   * activate in. That is what broke the first real host who tried this.
   *
   * Registered under Firebase's own dedicated scope rather than the root, so
   * it cannot fight with sw.js — the caching worker owns '/' and the two used
   * to overwrite each other on every page load, last registration winning.
   */
  async _messagingRegistration() {
    const SCOPE = '/firebase-cloud-messaging-push-scope';
    const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: SCOPE });
    if (reg.active) return reg;

    const worker = reg.installing || reg.waiting;
    if (!worker) {
      // Registered but with nothing to wait on — one update cycle usually
      // settles it; the caller reports a real failure if it does not.
      await reg.update().catch(() => {});
      return reg;
    }
    await new Promise((resolve) => {
      // 10s is far longer than activation takes; resolving anyway lets
      // getToken produce the real error rather than hanging the switch.
      const done = setTimeout(resolve, 10000);
      worker.addEventListener('statechange', () => {
        if (worker.state === 'activated' || worker.state === 'redundant') {
          clearTimeout(done);
          resolve();
        }
      });
    });
    return reg;
  }

  async getAndSaveToken(user) {
    // Every failure here used to end in a console.warn nobody reads, which is
    // how push stayed dead for its entire life without anyone noticing. The
    // reason now travels back to the caller AND to the server, because the two
    // remaining causes (a VAPID key that does not match the project's web-push
    // certificate, and a Firestore write the rules reject) are indistinguishable
    // from the outside and neither shows up anywhere a host or we would look.
    let token;
    try {
      // Explicit registration, awaited into life — never the SDK's implicit one.
      const swRegistration = await this._messagingRegistration();
      // Callers outside initFCM (pwa-install's auto-enable after install) can
      // arrive before initFCM has run, with messaging still null.
      if (!this.messaging) this.messaging = firebase.messaging();
      token = await this.messaging.getToken({ vapidKey: this.VAPID_KEY, serviceWorkerRegistration: swRegistration });
    } catch (err) {
      const reason = err?.code || err?.name || 'getToken-failed';
      console.error('❌ FCM getToken failed:', reason, err?.message || err);
      this.reportPushFailure('getToken', reason, err?.message);
      return { ok: false, reason, error: err };
    }
    if (!token) {
      console.error('❌ FCM getToken returned no token (permission granted but FCM declined)');
      this.reportPushFailure('getToken', 'empty-token');
      return { ok: false, reason: 'empty-token' };
    }

    this.fcmToken = token;
    try { localStorage.setItem(this.FCM_TOKEN_KEY, token); } catch (_) { /* private mode */ }

    const db = window.firebaseDb || window.db;
    if (!db) {
      this.reportPushFailure('save', 'no-db');
      return { ok: false, reason: 'no-db' };
    }
    try {
      await db.collection('fcm_tokens').doc(user.uid).set({
        tokens: firebase.firestore.FieldValue.arrayUnion(token),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        email: user.email || null
      }, { merge: true });
      console.log('FCM token saved to Firestore');
      return { ok: true };
    } catch (err) {
      const reason = err?.code || 'save-failed';
      console.error('❌ FCM token save failed:', reason, err?.message || err);
      this.reportPushFailure('save', reason, err?.message);
      // The token exists but nothing can send to it — treat it as not enabled
      // rather than leaving a switch that looks on and never fires.
      try { localStorage.removeItem(this.FCM_TOKEN_KEY); } catch (_) { /* private mode */ }
      this.fcmToken = null;
      return { ok: false, reason, error: err };
    }
  }

  /**
   * Beacon a push-enablement failure to the same endpoint the guest form uses.
   * Fire-and-forget and never throws: this is diagnostics, and a host must not
   * see a second failure because the report of the first one failed.
   *
   * No ids or addresses — the stage and the error code are the whole signal.
   */
  reportPushFailure(stage, reason, message) {
    try {
      const body = JSON.stringify({
        pagePath: location.pathname.slice(0, 200),
        pageLang: this.lang(),
        userAgent: navigator.userAgent,
        errors: [{
          level: 'error',
          text: `push-enable ${stage}: ${reason}${message ? ' — ' + String(message).slice(0, 200) : ''}`,
          at: 'pwa-notifications.js'
        }]
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/tracking/client-error', new Blob([body], { type: 'application/json' }));
      }
    } catch (_) { /* diagnostics must never cost the caller anything */ }
  }

  /**
   * Show a non-intrusive opt-in prompt for notifications. On the dashboard its
   * Enable opens the response-notifications window (dashboardQuickActions ->
   * components/notifications-modal.js), where the host picks email for the
   * event and/or push for this device: the same window as the dashboard's
   * bell, so the pill is a nudge to it, not a second settings path. Email is
   * the channel that reaches a host everywhere (push needs an installed app
   * on iPhone), so the pill does not offer push alone.
   */
  showNotificationOptIn() {
    // Don't show if user already dismissed
    if (localStorage.getItem('quikrsvp-notif-dismissed') === 'true') return;

    // Only show on dashboard pages
    if (!window.location.pathname.includes('dashboard')) return;

    // Wait a bit before showing
    setTimeout(() => {
      // Already notified about this event by email: nothing to nudge toward.
      if (window.currentFormData?.emailNotifications?.enabled) return;
      // One ask on screen at a time: the install strip (pwa-install.js) got
      // there first. Nothing is recorded, so the pill asks on a later visit.
      if (document.querySelector('#pwa-install-banner.show')) return;
      const banner = document.createElement('div');
      banner.id = 'notification-opt-in';
      banner.className = 'notification-opt-in';
      const c = this.copy();
      const rtl = this.lang() === 'he' || this.lang() === 'ar';
      banner.innerHTML = `
        <div class="d-flex align-items-center gap-2 p-2 px-3 bg-light border rounded-3 shadow-sm"
             dir="${rtl ? 'rtl' : 'ltr'}"
             style="position:fixed;bottom:20px;left:50%;transform:translateX(-50%);z-index:1050;max-width:420px;width:90%;">
          <i class="bi bi-bell text-primary fs-5"></i>
          <span class="small flex-grow-1">${c.ask}</span>
          <button class="btn btn-primary btn-sm" onclick="pwaNotifications.enableNotifications()">${c.enable}</button>
          <button class="btn btn-link btn-sm text-muted p-0" aria-label="${c.dismiss}" onclick="pwaNotifications.dismissNotificationOptIn()">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      `;
      document.body.appendChild(banner);
    }, 5000);
  }

  /**
   * User clicked "Enable" on the notification opt-in
   */
  async enableNotifications() {
    // The dashboard: open the window. The host has answered the nudge; the
    // bell / "Response Notifications" is its permanent home from here on.
    if (window.dashboardQuickActions?.openNotifications && window.NotificationsModal) {
      document.getElementById('notification-opt-in')?.remove();
      localStorage.setItem('quikrsvp-notif-dismissed', 'true');
      window.dashboardQuickActions.openNotifications();
      return;
    }
    // Take the banner off screen, but do NOT record a dismissal yet. This used
    // to set the never-ask-again flag BEFORE the browser prompt, so a host who
    // clicked Enable and then ignored the prompt lost their only route in — the
    // banner was the sole entry point and nothing ever cleared the flag.
    const banner = document.getElementById('notification-opt-in');
    if (banner) banner.remove();

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      const user = window.firebaseAuth?.currentUser;
      if (user) await this.getAndSaveToken(user);
      else this.reportPushFailure('enable', 'signed-out');
    }
    // 'default' means the prompt was dismissed without an answer — leave the
    // flag alone so the next visit can ask again. Only a real decision, or the
    // × button, is remembered.
    if (permission === 'granted' || permission === 'denied') {
      localStorage.setItem('quikrsvp-notif-dismissed', 'true');
    }
  }

  /**
   * Dismiss the notification opt-in banner
   */
  dismissNotificationOptIn() {
    const banner = document.getElementById('notification-opt-in');
    if (banner) banner.remove();
    localStorage.setItem('quikrsvp-notif-dismissed', 'true');
  }

  /** Locale of the page, from the path prefix — the same rule my-projects uses. */
  lang() {
    const m = window.location.pathname.match(/^\/(es|fr|de|pt|he|ar)\//);
    return m ? m[1] : 'en';
  }

  /** Banner copy. Small enough to live here rather than in a translation file. */
  copy() {
    const T = {
      en: { ask: 'Get notified when guests RSVP?', enable: 'Enable', dismiss: 'Dismiss' },
      es: { ask: '¿Quieres un aviso cuando confirmen?', enable: 'Activar', dismiss: 'Descartar' },
      fr: { ask: 'Être averti quand un invité répond ?', enable: 'Activer', dismiss: 'Ignorer' },
      de: { ask: 'Bei Antworten benachrichtigt werden?', enable: 'Aktivieren', dismiss: 'Schließen' },
      pt: { ask: 'Quer ser avisado quando responderem?', enable: 'Ativar', dismiss: 'Dispensar' },
      he: { ask: 'לקבל התראה כשמוזמנים מאשרים?', enable: 'הפעלה', dismiss: 'סגירה' },
      ar: { ask: 'هل تريد تنبيهاً عند رد الضيوف؟', enable: 'تفعيل', dismiss: 'إغلاق' }
    };
    return T[this.lang()] || T.en;
  }

  /**
   * Whether push can work here at all: the APIs exist, the page is a secure
   * context, and Firebase says the browser is supported. iOS Safari only
   * qualifies once the app is installed to the home screen, which is why this
   * is a runtime check and not a feature flag.
   */
  /** The sample dashboard (?demo=1): view only, every write blocked. */
  isDemo() {
    return !!window.isDemoMode || /[?&]demo=1(?:&|$)/.test(window.location.search);
  }

  pushSupported() {
    try {
      // Not on the demo: its writes are blocked, so the switch could never
      // stick. Unsupported hides the row in the notifications window too.
      if (this.isDemo()) return false;
      if (!('Notification' in window) || !('serviceWorker' in navigator)) return false;
      if (typeof firebase === 'undefined' || !firebase.messaging) return false;
      if (typeof firebase.messaging.isSupported === 'function' && !firebase.messaging.isSupported()) return false;
      return true;
    } catch (_) {
      return false;
    }
  }

  /**
   * What a settings UI needs to draw the toggle:
   *   supported  — false hides the row entirely
   *   permission — 'granted' | 'denied' | 'default'
   *   enabled    — granted AND this device has a token registered
   * 'denied' is the one state the page cannot fix: only the host can unblock
   * the site in their own browser settings, so the UI says so instead of
   * offering a switch that could not work.
   */
  pushState() {
    if (!this.pushSupported()) return { supported: false, permission: 'unsupported', enabled: false };
    const permission = Notification.permission;
    let token = null;
    try { token = localStorage.getItem(this.FCM_TOKEN_KEY); } catch (_) { /* private mode */ }
    return { supported: true, permission, enabled: permission === 'granted' && !!token };
  }

  /**
   * Turn push on for THIS device. Must be called from a user gesture —
   * Notification.requestPermission() is ignored otherwise.
   */
  async enablePush() {
    if (!this.pushSupported()) return { ok: false, reason: 'unsupported' };
    try {
      const permission = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
      if (permission !== 'granted') return { ok: false, reason: permission };

      const user = window.firebaseAuth?.currentUser;
      if (!user) return { ok: false, reason: 'signed-out' };

      if (!this.messaging) this.messaging = firebase.messaging();
      const res = await this.getAndSaveToken(user);
      // A permission grant that yields no token is a real failure (a rejected
      // VAPID key, a blocked service worker, a refused write) and must not
      // report success. Carry the specific reason so the UI and the beacon
      // agree on what actually went wrong.
      if (res && res.ok === false) return res;
      return this.pushState().enabled ? { ok: true } : { ok: false, reason: 'no-token' };
    } catch (err) {
      console.warn('enablePush failed:', err);
      return { ok: false, reason: 'error' };
    }
  }

  /**
   * Turn push off for THIS device. Browser permission cannot be revoked from
   * script, so "off" means the server stops having somewhere to send: the
   * token is removed from fcm_tokens, which is the only list sendNewResponsePush
   * reads. Unregistering with FCM as well is best-effort cleanup.
   */
  async disablePush() {
    let token = null;
    try { token = localStorage.getItem(this.FCM_TOKEN_KEY); } catch (_) { /* private mode */ }

    const user = window.firebaseAuth?.currentUser;
    const db = window.firebaseDb || window.db;
    if (token && user && db) {
      try {
        await db.collection('fcm_tokens').doc(user.uid).update({
          tokens: firebase.firestore.FieldValue.arrayRemove(token)
        });
      } catch (err) {
        // A doc that never existed is already "off".
        if (err?.code !== 'not-found') console.warn('disablePush: token removal failed:', err);
      }
    }
    try {
      if (this.messaging && typeof this.messaging.deleteToken === 'function') {
        await this.messaging.deleteToken();
      }
    } catch (_) { /* best effort */ }
    try { localStorage.removeItem(this.FCM_TOKEN_KEY); } catch (_) { /* private mode */ }
    this.fcmToken = null;
    return { ok: true };
  }

  /**
   * Show an in-app notification toast when a push arrives while app is open
   */
  showInAppNotification(payload) {
    const title = payload.notification?.title || 'New RSVP Response';
    const body = payload.notification?.body || 'Someone just responded to your form!';

    // Use the app's existing toast system if available
    if (typeof window.showToast === 'function') {
      window.showToast(`${title}: ${body}`, 'info');
    } else {
      // Fallback: browser notification
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: '/assets/favicons/android-chrome-192x192.png',
          badge: '/assets/favicons/favicon-48x48.png'
        });
      }
    }
  }
}

// Initialize. Also exposed on `window`: a top-level `const` lives in the global
// LEXICAL scope, which inline onclick= handlers can reach but `window.x` cannot,
// and my-projects.js needs the latter.
const pwaNotifications = new PWANotificationManager();
window.pwaNotifications = pwaNotifications;

// Auto-init when DOM is ready and we're on a dashboard page
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => pwaNotifications.init());
} else {
  pwaNotifications.init();
}
